import React, { useState, useEffect } from "react";
import { PieChart, Sparkles, Database, Layers3, Activity, Users, Calendar, ArrowUpRight, MessageSquareDashed, ScanLine } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function UsageOverviewPanel() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("day"); // 'day' or 'month'
  const [date, setDate] = useState("");
  
  const [usageData, setUsageData] = useState(null);
  const [ocrTodaySummary, setOcrTodaySummary] = useState(null);

  useEffect(() => {
    // Set default date based on period
    const now = new Date();
    if (period === "day") {
      setDate(now.toISOString().split("T")[0]); // YYYY-MM-DD
    } else {
      setDate(now.toISOString().slice(0, 7)); // YYYY-MM
    }
  }, [period]);

  useEffect(() => {
    if (!date) return;
    
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      const today = new Date().toISOString().split("T")[0];
      try {
        const [usageRes, ocrRes] = await Promise.all([
          authFetch(`${API_BASE}/admin/usage-report?period=${period}&date=${date}`),
          authFetch(`${API_BASE}/admin/ocr-usage?period=day&date=${today}&limit=1`),
        ]);
        const usageJson = await usageRes.json();
        const ocrJson = ocrRes.ok ? await ocrRes.json() : null;

        if (isMounted) {
          setUsageData(usageJson);
          setOcrTodaySummary(ocrJson?.summary || null);
        }
      } catch (err) {
        console.error("Failed to load admin overview:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    loadData();
    return () => { isMounted = false; };
  }, [authFetch, period, date]);

  const fmt = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));
  const fmtCurrency = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 4 }).format(Number(value || 0));

  const summary = usageData?.summary || { total_tokens: 0, paid_tokens: 0, vllm_tokens: 0, estimated_cost_usd: 0 };
  const rows = usageData?.rows || [];
  
  // Calculate savings: Assuming vLLM tokens would have costed around $0.50 per 1M tokens (average cheap model cost)
  const estimatedSavingsUsd = (summary.vllm_tokens / 1000000) * 0.50;

  return (
    <div className="space-y-5 text-sm pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-bold text-lg text-slate-800 tracking-tight">
          <PieChart className="text-violet-600" size={20} />
          ภาพรวมการใช้งาน AI
        </h2>
      </div>

      {/* Filters */}
      <div className="flex gap-2 bg-slate-100/80 p-1.5 rounded-xl border border-slate-200/60">
        <button
          onClick={() => setPeriod("day")}
          className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all duration-300 ${
            period === "day" ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          รายวัน
        </button>
        <button
          onClick={() => setPeriod("month")}
          className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all duration-300 ${
            period === "month" ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          รายเดือน
        </button>
      </div>

      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <input
          type={period === "day" ? "date" : "month"}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200/80 rounded-xl text-sm font-medium text-slate-700 shadow-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 outline-none transition-all"
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-4 animate-fadeIn">
          
          {/* Main Stat Card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white shadow-lg shadow-violet-500/20">
            <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 text-white/80 text-xs font-medium uppercase tracking-wider mb-1">
                <Activity size={14} />
                โทเคนรวมทั้งหมด (Tokens)
              </div>
              <div className="text-4xl font-extrabold tracking-tight">
                {fmt(summary.total_tokens)}
              </div>
              
              <div className="mt-5 grid grid-cols-2 gap-4 border-t border-white/20 pt-4">
                <div>
                  <div className="text-white/70 text-[10px] uppercase tracking-wider mb-1">ค่าใช้จ่ายจริง (API)</div>
                  <div className="text-lg font-bold text-emerald-300">{fmtCurrency(summary.estimated_cost_usd)}</div>
                </div>
                <div>
                  <div className="text-white/70 text-[10px] uppercase tracking-wider mb-1">ประหยัดได้ (vLLM)</div>
                  <div className="text-lg font-bold text-cyan-300">~{fmtCurrency(estimatedSavingsUsd)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown Cards */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
            <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm min-h-[110px]">
              <div className="flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-wider mb-2">
                <Sparkles size={14} className="text-amber-500" />
                โมเดลภายนอก
              </div>
              <div className="text-xl font-bold text-slate-800">{fmt(summary.paid_tokens)}</div>
              <div className="text-[10px] text-slate-400 mt-1">โทเคนที่เสียค่าใช้จ่าย</div>
            </div>
            
            <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm min-h-[110px]">
              <div className="flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-wider mb-2">
                <Database size={14} className="text-emerald-500" />
                โมเดลภายใน (vLLM)
              </div>
              <div className="text-xl font-bold text-slate-800">{fmt(summary.vllm_tokens)}</div>
              <div className="text-[10px] text-slate-400 mt-1">โทเคนฟรี 100%</div>
            </div>
          </div>

          {/* Active Users & Feedback Links */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 mt-4">
            {/* Active Users */}
            <div 
              onClick={() => navigate("/admin/active-users")}
              className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm min-h-[116px] flex items-center justify-between cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-300 group"
            >
              <div>
                <div className="flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-wider mb-1">
                  <Users size={14} className="text-blue-500" />
                  ผู้ใช้งาน{period === "day" ? "ในวันนี้" : "ในเดือนนี้"}
                </div>
                <div className="text-2xl font-bold text-slate-800">{fmt(summary.active_users)} <span className="text-sm font-medium text-slate-400">คน</span></div>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                <ArrowUpRight size={20} className="text-blue-600 group-hover:text-white transition-colors duration-300" />
              </div>
            </div>

            {/* Feedback Link */}
            <div 
              onClick={() => navigate("/admin/feedback")}
              className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm min-h-[116px] flex items-center justify-between cursor-pointer hover:shadow-md hover:border-amber-300 transition-all duration-300 group"
            >
              <div>
                <div className="flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-wider mb-1">
                  <MessageSquareDashed size={14} className="text-amber-500" />
                  เสียงจากผู้ใช้งาน
                </div>
                <div className="text-base font-bold text-slate-800 mt-1 leading-tight">ดูข้อเสนอแนะ</div>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center group-hover:bg-amber-500 transition-colors duration-300">
                <ArrowUpRight size={20} className="text-amber-600 group-hover:text-white transition-colors duration-300" />
              </div>
            </div>

            {/* OCR Usage Link */}
            <div
              onClick={() => navigate("/admin/ocr-usage")}
              className="bg-white rounded-2xl p-4 border border-violet-200/60 shadow-sm min-h-[116px] flex items-center justify-between cursor-pointer hover:shadow-md hover:border-violet-400 transition-all duration-300 group"
            >
              <div>
                <div className="flex items-center gap-2 text-violet-600 text-[11px] font-semibold uppercase tracking-wider mb-1">
                  <ScanLine size={14} className="text-violet-500" />
                  Typhoon OCR วันนี้
                </div>
                <div className="text-2xl font-bold text-slate-800">
                  {fmt(ocrTodaySummary?.active_users)}{" "}
                  <span className="text-sm font-medium text-slate-400">คน</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center group-hover:bg-violet-600 transition-colors duration-300">
                <ArrowUpRight size={20} className="text-violet-600 group-hover:text-white transition-colors duration-300" />
              </div>
            </div>
          </div>

          {/* Models Breakdown */}
          <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm mt-4">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Layers3 size={14} className="text-indigo-500" />
              สัดส่วนการใช้โมเดล
            </h3>
            
            {rows.length === 0 ? (
              <div className="text-center text-slate-400 text-xs py-4">ไม่มีข้อมูลในวันที่เลือก</div>
            ) : (
              <div className="space-y-3">
                {rows.map((row, idx) => {
                  const percent = summary.total_tokens > 0 ? (row.tokens_used / summary.total_tokens) * 100 : 0;
                  const isFree = String(row.provider).toLowerCase() === "vllm";
                  return (
                    <div key={idx} className="relative">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700 truncate pr-2">{row.model}</span>
                        <span className="font-semibold text-slate-900">{fmt(row.tokens_used)}</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${isFree ? 'bg-emerald-400' : 'bg-amber-400'}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[9px] text-slate-400">{isFree ? "ฟรี" : "เสียเงิน"}</span>
                        <span className="text-[9px] text-slate-400">{percent.toFixed(1)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="text-center text-[10px] text-slate-400 mt-4 px-4">
            ข้อมูลนี้ช่วยให้ผู้บริหารเห็นความคุ้มค่าของการลงทุนใน AI (OpenRouter) เทียบกับการใช้ทรัพยากรภายใน (vLLM)
          </div>
        </div>
      )}
    </div>
  );
}
